import { inject, Injectable } from '@angular/core';
import { LocalCameraService } from 'src/app/modules/camera/services/local-camera.service';
import { NativeCameraPublishingService } from 'src/app/modules/camera/services/native-camera-publishing.service';

import { LoadingService } from '../loading.service';
import { UserMediaService } from '../user-media.service';
import { JanusEventsService, JanusEventType } from './janus-events.service';
import { JanusLoggingService } from './janus-logging.service';
import { JanusPublisherService } from './janus-publisher.service';
import { JanusStateService, PublishingMediaDevices } from './janus-state.service';
import { JanusUserMediaService } from './janus-user-media.service';

@Injectable({
    providedIn: 'root',
})
export class CurrentDeviceCameraReconnectService {
    private janusStateService: JanusStateService = inject(JanusStateService);
    private janusPublisherService: JanusPublisherService = inject(JanusPublisherService);
    private janusEventsService: JanusEventsService = inject(JanusEventsService);
    private janusLoggingService: JanusLoggingService = inject(JanusLoggingService);
    private userMediaService: UserMediaService = inject(UserMediaService);
    private janusUserMediaService: JanusUserMediaService = inject(JanusUserMediaService);
    private loadingService: LoadingService = inject(LoadingService);
    private localCameraService: LocalCameraService = inject(LocalCameraService);
    private nativeCameraPublishingService: NativeCameraPublishingService = inject(NativeCameraPublishingService);

    public async resetCurrentDevice(
        previousType: JanusEventType,
        media: PublishingMediaDevices,
        dismissLoader: boolean
    ): Promise<void> {
        try {
            this.janusLoggingService.log('[CameraReconnect] Reset current device using WebRTC camera');
            await this.restartWebRtcCurrentDevice(previousType, media);
        } catch (error) {
            this.janusLoggingService.error('Error resetting current-device stream', error);
        } finally {
            if (dismissLoader) {
                await this.loadingService.dismissLoader();
            }
        }
    }

    public reconnectCurrentDeviceAfterServerReconnect(janusServerHost: string, previousType: JanusEventType): void {
        if (this.nativeCameraPublishingService.hasLastJanusServerHost(janusServerHost)) {
            this.janusLoggingService.log('[CameraReconnect] Reconnecting native current-device camera', {
                janusServerHost,
            });
            this.restartNativeCurrentDevice(previousType).catch((error) =>
                this.janusLoggingService.error('Error restarting native camera after Janus reconnect', error)
            );
            return;
        }

        if (!this.isOwnVideoRoomServer(janusServerHost)) {
            return;
        }

        if (!this.shouldReconnectWebRtcCurrentDevice()) {
            this.janusLoggingService.log('[CameraReconnect] Skipping WebRTC current-device reconnect', {
                janusServerHost,
                hasMedia: !!this.janusStateService.videoPublisher().media,
                roomId: this.janusStateService.videoRoom().room_id,
                cameraType: this.janusStateService.videoRoom().camera_type,
            });
            return;
        }

        this.janusLoggingService.log('[CameraReconnect] Reconnecting WebRTC current-device camera', {
            janusServerHost,
        });

        const videoRoom = this.janusStateService.videoRoom();
        this.localCameraService
            .cleanupAndAttachPublishingMedia()
            .then(() => {
                if (this.janusStateService.videoRoom() !== videoRoom || !this.shouldReconnectWebRtcCurrentDevice()) {
                    return;
                }

                this.janusEventsService.emitEvent({
                    type: JanusEventType.NewRoom,
                    previousType,
                    janusPublishData: videoRoom,
                });
            })
            .catch((error) => {
                this.janusLoggingService.error('Error reconnecting WebRTC current device after Janus reconnect', error);
            });
    }

    public isOwnVideoRoomServer(janusServerHost: string): boolean {
        const videoRoom = this.janusStateService.videoRoom();

        return janusServerHost === videoRoom.janus_server_host && !!videoRoom.room_id;
    }

    private async restartNativeCurrentDevice(previousType: JanusEventType): Promise<void> {
        const janusPublishData = await this.nativeCameraPublishingService.restart();
        if (!janusPublishData) {
            this.janusLoggingService.log('[CameraReconnect] Native current-device restart returned no room data', {
                previousType,
            });
            return;
        }

        this.janusEventsService.emitEvent({
            type: JanusEventType.NewRoom,
            previousType,
            janusPublishData,
        });
    }

    private async restartWebRtcCurrentDevice(
        previousType: JanusEventType,
        media: PublishingMediaDevices
    ): Promise<void> {
        const currentMedia = media ?? this.janusStateService.videoPublisher().media;
        if (!currentMedia) {
            this.janusLoggingService.log('[CameraReconnect] Cannot reset WebRTC current device without media', {
                previousType,
            });
            return;
        }

        const stream = await this.userMediaService.getUserMedia(currentMedia);
        this.userMediaService.setStreamAsOwnUserMedia(this.janusStateService.videoUserMedia, stream, true);

        try {
            const janusPublishData = await this.janusPublisherService.startPublishing(
                'video',
                currentMedia,
                this.janusStateService.videoUserMedia()
            );

            this.janusEventsService.emitEvent({
                type: JanusEventType.NewRoom,
                previousType,
                janusPublishData,
            });
        } catch (error) {
            this.janusStateService.videoUserMedia.update((userMedia) => ({
                ...this.janusUserMediaService.cleanupUserMedia(
                    userMedia,
                    { video: true, audio: false },
                    { video: true, audio: false }
                ),
            }));
            throw error;
        }
    }

    private shouldReconnectWebRtcCurrentDevice(): boolean {
        const videoRoom = this.janusStateService.videoRoom();
        const videoPublisher = this.janusStateService.videoPublisher();

        return (
            !!videoRoom.room_id &&
            !!videoRoom.janus_server_host &&
            !!videoPublisher.media &&
            videoPublisher.isPublishing &&
            !this.janusStateService.usingExternal
        );
    }
}
